import {
  type Camera,
  type Color,
  type ColorRepresentation,
  type Object3D,
  PCFSoftShadowMap,
  SRGBColorSpace,
  WebGLRenderer,
} from '@seddi/three';

import * as FlagsModule from '../flags/flagsModule';
import { FeatFlag } from '../flags/flagTypes';

export class ThreeRenderer {
  canvas: HTMLCanvasElement;
  contextAttributes: WebGLContextAttributes;
  renderer: WebGLRenderer;

  constructor() {
    this.contextAttributes = {
      alpha: false,
      antialias: true,
      preserveDrawingBuffer: false,
      stencil: false,
    };
  }

  configurePreserveDrawingBuffer(value: boolean) {
    this.contextAttributes.preserveDrawingBuffer = value;
  }

  configureAlpha(value: boolean) {
    this.contextAttributes.alpha = value;
  }

  init(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // create GPU renderer
    this.renderer = new WebGLRenderer({
      alpha: this.contextAttributes.alpha,
      antialias: FlagsModule.isEnabled(FeatFlag.DEPTH_PEELING),
      canvas: this.canvas,
      context: canvas.getContext('webgl2', this.contextAttributes),
    });

    // configure GPU renderer
    this.renderer.autoClear = false;
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.useLegacyLights = false;
  }

  dispose() {
    // free GPU related resources
    this.renderer && this.renderer.dispose();
    this.renderer = undefined;
    // free references
    this.canvas = undefined;
  }

  getClearColor(target: Color) {
    this.renderer.getClearColor(target);
  }

  setClearColor(background: ColorRepresentation, alpha = 1) {
    this.renderer.setClearColor(background, alpha);
  }

  setSize(width: number, height: number, devicePixelRatio = 1) {
    // NOTE: use setDrawingBufferSize when sceneRendererOIT is out of its implementation
    this.renderer.setPixelRatio(devicePixelRatio);
    this.renderer.setSize(width, height, false);
  }

  clearDepth() {
    this.renderer.clearDepth();
  }

  render(scene: Object3D, camera: Camera) {
    this.renderer.clear();
    this.renderer.render(scene, camera);
  }
}
